import { Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Observable, repeat, retry, Subject } from 'rxjs';
import { SocketMessage, SocketMessageType } from '../models/websocket';
import { NotificationService } from './notification.service';
import { IncidentService } from './incident.service';
import { AuthService } from '../auth.service';
import { UserType } from './enums';
import { BDTechIncidentStatusUpdate, Document, Incident, IncidentEtaUpdate, IncidentLocation, ServiceProviderStatusUpdate } from '../models/incident';
import { SharedDataService } from './shareddata.service';
import { WhatsappService } from '../whatsapp/whatsapp.service';
import { ChatService } from './chat.service';
import { ChatSenderType, IncidentChatMessageCreated, ChatEscalation } from '../models/chat';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WebsocketsService {
  
  public socket$: WebSocketSubject<any>;

 
  constructor(
    private notification: NotificationService, 
    private incidentService: IncidentService,
    private authService: AuthService,
    private sharedDataService: SharedDataService,
    private whatsappService: WhatsappService,
    private chatService: ChatService
  ) {
    this.chatService.escalationChanges.subscribe(escalation => {
      if (!this.authService.isClientCallCentre) return;
      if (escalation.escalationAcknowledgedDate) this.notification.clearChatEscalation(escalation.chatMessageId);
      else this.notification.showChatEscalation(escalation.incidentId, escalation.incidentNumber || String(escalation.incidentId),
        escalation.chatMessageId, escalation.message || 'Attachment');
    });
  }

  public connect(url: string): WebSocketSubject<any> {
    this.socket$ = webSocket({ url, openObserver: { next: () => {
      if (this.authService.isClientCallCentre) this.chatService.socketOpened.next();
    } } });
    console.log("[WS] Connected to ", url);
    return this.socket$;
  }

  public connectIncident(incidentId: number, participantType: UserType): Observable<any> {
    const url = `${environment.wsUrl.replace(/\/connect\/?$/, '/incident')}/${incidentId}/${participantType}`;
    // Keep this connection separate from the logged-in socket used by SendMessage.
    return webSocket({ url }).pipe(
      repeat({ delay: 5000 }),
      retry({ delay: 5000 })
    );
  }

  public SendMessage(msg: any) {
    this.socket$.next(msg);
  }

  public getIncidentUpdate(msg: string, incidentId: number): Incident | null {
    try {
      const message = JSON.parse(msg) as SocketMessage;
      if (message?.type !== SocketMessageType.UpdateIncident) return null;
      const incident = JSON.parse(message.message) as Incident;
      return incident?.incidentId === incidentId ? incident : null;
    } catch {
      return null;
    }
  }

  public handleChatMessage(msg: string, viewer: ChatSenderType, incidentId?: number): IncidentChatMessageCreated | null {
    try {
      const message = JSON.parse(msg) as SocketMessage;
      if (message?.type !== SocketMessageType.IncidentChatMessageCreated) return null;
      const update = JSON.parse(message.value!) as IncidentChatMessageCreated;
      if (Number.isSafeInteger(update?.incidentId) && update.incidentId > 0 &&
          Number.isSafeInteger(update?.chatMessageId) && update.chatMessageId > 0 &&
          (incidentId === undefined || incidentId === update.incidentId)) {
        this.chatService.notifyMessageCreated(update, viewer);
        return update;
      }
    } catch { /* Ignore malformed chat notifications. */ }
    return null;
  }

  public HandleMessage(msg: string) {
    //check if the message is a valid SocketMessage
    console.debug("[WS] Received message:", msg);
    let parsedMsg: SocketMessage;
    try {
      parsedMsg = JSON.parse(msg) as SocketMessage;
    }
    catch (e) {
      console.error("[WS] Invalid message format", msg);
      return;
    }

    // Handle the valid SocketMessage
    switch (parsedMsg.type) {
      case SocketMessageType.IncidentChatMessageEscalated:
      case SocketMessageType.IncidentChatMessageEscalationAcknowledgement: {
        if (!this.authService.isClientCallCentre) break;
        try {
          const escalation = JSON.parse(parsedMsg.value!) as ChatEscalation;
          if (!Number.isSafeInteger(escalation?.incidentId) || escalation.incidentId <= 0 ||
              !Number.isSafeInteger(escalation?.chatMessageId) || escalation.chatMessageId <= 0 ||
              !escalation.escalatedDate || (parsedMsg.type === SocketMessageType.IncidentChatMessageEscalationAcknowledgement &&
              !escalation.escalationAcknowledgedDate)) break;
          this.chatService.trackEscalations([escalation]);
          this.chatService.messageCreated.next({ incidentId: escalation.incidentId,
            chatMessageId: escalation.chatMessageId, viewerType: ChatSenderType.Callcentre });
        } catch { /* Ignore malformed escalation events. */ }
        break;
      }
      case SocketMessageType.IncidentChatMessageCreated: {
        if (!this.authService.isClientCallCentre) break;
        this.handleChatMessage(msg, ChatSenderType.Callcentre);
        break;
      }
      case SocketMessageType.InfoMessage:
        this.notification.showInfo(parsedMsg.message);
        break;
      case SocketMessageType.ErrorMessage:
        this.notification.showError(parsedMsg.message);
        break;
      case SocketMessageType.WarningMessage:
        this.notification.showWarning(parsedMsg.message);
        break;
      case SocketMessageType.CreateIncident:
        let incident: Incident = JSON.parse(parsedMsg.message) as Incident;
        if (this.authService.user?.userType === UserType.truckdriver) {
          this.incidentService.DriverIncident = incident;
        }
        else {
          this.incidentService.updateOrAddIncidentToList(incident);
        }
        break;
      case SocketMessageType.UpdateIncident:
        let updatedIncident: Incident = JSON.parse(parsedMsg.message) as Incident;
        if (this.authService.isClientCallCentre && updatedIncident.chatEscalations)
          this.chatService.trackEscalations(updatedIncident.chatEscalations);
        // let index = this.incidentService.operatorIncidents.findIndex(incident => incident.incidentId === updatedIncident.incidentId);
        // if (index !== -1) {
        //   console.log("[WS] Updating incident in list:", updatedIncident.incidentId);
          
        //   //create new location object to trigger change detection
        //   const loc = new IncidentLocation();
        //   loc.latitude = updatedIncident.location.latitude;
        //   loc.longitude = updatedIncident.location.longitude;
        //   loc.description = updatedIncident.location.description;
        //   updatedIncident.location = loc;
        //   this.incidentService.operatorIncidents[index] = updatedIncident;
        //   console.log("[WS] Updated incident location object to trigger change detection:", updatedIncident.location);
        // } else {
        //   this.incidentService.operatorIncidents.push(updatedIncident);
        // }
        this.incidentService.updateOrAddIncidentToList(updatedIncident);
        
        if (this.authService.user?.userType === UserType.truckdriver) {
          if (this.incidentService.DriverIncident?.incidentId === updatedIncident.incidentId) {
            this.incidentService.DriverIncident = updatedIncident;
            console.log("[WS] Updated DriverIncident:", this.incidentService.DriverIncident);            
          }
          else if (!this.incidentService.DriverIncident || this.incidentService.DriverIncident.incidentId === 0) {
            this.incidentService.DriverIncident = updatedIncident;
            console.log("[WS] Set DriverIncident:", this.incidentService.DriverIncident);
          }
          else {
            this.notification.showWarning("A different breakdown was logged while you had one open. Please check your breakdowns list.");
          }
        }
        break;
      case SocketMessageType.WhatsappIncident:
        this.notification.showInfo("New incoming breakdown from WhatsApp", 250);
        let waMsg = this.whatsappService.addNewIncomingMessage(parsedMsg);
        break;
      case SocketMessageType.WhatsappOther:
        this.whatsappService.addNewIncomingMessage(parsedMsg);
        break;
      case SocketMessageType.ProofOfWorkDocument:
        
        let doc = JSON.parse(parsedMsg.value!) as Document;
        let inc = this.incidentService.openIncidents().find(incident => incident.incidentId === doc.incidentId);
        console.log("Incident for document?", inc === undefined ? "No" : "Yes");
        
        if (inc) {
          if (this.authService.isClientCallCentre) {
            this.whatsappService.addNewIncomingMessage(parsedMsg);
          }
          //Check if doc exists before pushing it
          if (!inc.documents.find(d => d.documentId === doc.documentId)) {
            inc.documents.push(doc);
            console.log("[WS] Added document to incident:", doc, inc);
            this.notification.showInfo("New attachment received - for breakdown #" + inc.incidentId);
          }
          
        }
        else if (this.incidentService.DriverIncident && this.incidentService.DriverIncident.incidentId === doc.incidentId) {
          let driverInc = this.incidentService.DriverIncident;
          if (!driverInc.documents.find(d => d.documentId === doc.documentId)) {
            driverInc.documents.push(doc);
            console.log("[WS] Added document to DriverIncident:", doc, driverInc);
            if (this.authService.isTruckDriver) {
              this.notification.showInfo("New Proof of Work document received - for your breakdown #" + driverInc.incidentId);
            }
          }
        }
        else {
          //if we don't have an incident loaded, just notify and add to shared messages
          //this.sharedDataService.messages.push(parsedMsg);
          this.whatsappService.addNewIncomingMessage(parsedMsg);
          this.notification.showInfo("New attachment received. Please check open messages.");
        }
        break;
      case SocketMessageType.IncidentLocationUpdate:
        let waMsgLoc = this.whatsappService.addNewIncomingMessage(parsedMsg);
        this.notification.showInfo("New breakdown from location received. Please check open messages.");
        break;
      case SocketMessageType.IncidentETAUpdate:
        let etaUpdate = JSON.parse(parsedMsg.value!) as IncidentEtaUpdate;
        this.incidentService.emitIncidentEtaUpdate(etaUpdate);
        //find incident to update
        let incidentToUpdate = this.incidentService.openIncidents().find(incident => incident.incidentId === etaUpdate.incidentId);
        if (incidentToUpdate) {
          const currentEta = new Date(incidentToUpdate.breakdownTechnicianDriverEta);
          incidentToUpdate.breakdownTechnicianDriverEta = new Date(etaUpdate.newEta);
          //calculate difference in minutes
          const diffMs = incidentToUpdate.breakdownTechnicianDriverEta.getTime() - currentEta.getTime();
          const diffMins = Math.round(diffMs / 60000);
          this.notification.showInfo("Breakdown #" + incidentToUpdate.incidentId + " ETA updated to " + incidentToUpdate.breakdownTechnicianDriverEta.toLocaleString() + " (" + (diffMins >= 0 ? "+" : "") + diffMins + " mins)", 4000);
          
        }
        break;
      case SocketMessageType.ServiceProviderStatusUpdate:
        let statusUpdate = JSON.parse(parsedMsg.value!) as ServiceProviderStatusUpdate;
        this.incidentService.$serviceProviderStatusUpdate.emit(statusUpdate);
        // this.notification.showInfo("Service Provider status updated for Client ID " + statusUpdate.clientId);
        break;
      case SocketMessageType.ServiceProviderRejected:
        //let rejectedIncidentId = Number(parsedMsg.value);
        //this.notification.showWarning("Service Provider has rejected incident #" + rejectedIncidentId);
        if (this.authService.isClientCallCentre) {
          this.notification.showWarningWithButton(
            parsedMsg.message,
            parsedMsg.action1Text || 'View Incident',
            parsedMsg.action1Url || '/incidents'
          );
        }
        break;
      case SocketMessageType.BDTechComplete:
        if (this.authService.isClientCallCentre) {
          this.notification.showInformationWithButton(
            parsedMsg.message,
            parsedMsg.action1Text || 'View Incident',
            parsedMsg.action1Url || '/incidents'
          );
        }
        break;
      case SocketMessageType.BDTechAccepted:
        if (this.authService.isClientCallCentre) {
          this.notification.showSuccessPinned(
            parsedMsg.message
          );
        }
        let statusUpdateBDTech = JSON.parse(parsedMsg.value!) as BDTechIncidentStatusUpdate;
        this.incidentService.$bdTechIncidentStatusUpdate.emit(statusUpdateBDTech);
        break;
      case SocketMessageType.WhatsappMessageLinked:
        const incidentId = Number(parsedMsg.value);
        this.whatsappService.linkMessageToIncident(parsedMsg.whatsappMessageId, incidentId);
        this.notification.showInfo("A WhatsApp message has been linked to an incident. Please check open messages.");
        break;
    }
  }
  
}
